/**
 * B2cSellerNoLocation — the seller line the desktop wishlist Select Size popup
 * shows when there is no delivery location yet (`b2cSellerConfig.isEnabled`).
 *
 * With no pincode / address, `fetchDeliveryDetailsV2` stops at its
 * `hasVisibleDeliveryLocation` guard, so there is no scopedView response and no
 * SLA cards to render — `DeliverySLA` returns null on empty, which takes its
 * seller row with it. The design still names a seller for the picked size, off
 * the catalogue (wishlist `variantOptions[].sellerName`), with a note that the
 * real one depends on where it ships to.
 *
 * Deliberately not routed through DeliverySLA: there is no view model here, and
 * making that component render on empty options would change the PDP and cart
 * too. The seller row markup and classes are the card's, so the two states look
 * alike.
 */
import React, { FC } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import properties from '../../../modules/ajio-util/properties';
import { getB2cSellerConfig } from '../../../refresh/selectors/featureFlagsSelector';
import styles from './index.pcss';

const DEFAULT_SELLER_CHANGE_NOTE = 'Seller may change based on address/pin code selected';

const cx = classNames.bind(styles);

const imageBaseUrl = properties.getProperty('imageBaseUrl');
/** Same storefront glyph the SLA card's seller row uses. */
const SELLER_ICON = `${imageBaseUrl}/static/img/b2c-seller-icon.svg`;
const SELLER_ICON_LUXE = `${imageBaseUrl}/static/img/b2c-seller-icon-luxe.svg`;

interface StateProps {
  /** b2cSellerConfig.sellerChangeNote */
  note?: string;
}

interface OwnProps {
  /** Catalogue seller for the selected size. Nothing renders without one. */
  sellerName?: string;
  /** luxe theme, forwarded from the host popup (newDesignLuxe) */
  luxe?: boolean;
  /** Cart / wishlist size popup — seller row sits on #F5F5F5 instead of PDP white. */
  onDialogSurface?: boolean;
}

type Props = StateProps & OwnProps;

const B2cSellerNoLocation: FC<Props> = ({ sellerName, note, luxe = false, onDialogSurface = false }) => {
  if (!sellerName) return null;

  const resolvedNote = note || DEFAULT_SELLER_CHANGE_NOTE;

  return (
    // The theme classes carry the seller row's background, so they come along.
    // `.sla-footer::before` supplies the "*" under luxe, hence the plain copy.
    <div className={cx('sla', 'sla-b2c', { luxe, 'sla--dialog': onDialogSurface })}>
      <div className={cx('sla-seller')}>
        <img
          src={luxe ? SELLER_ICON_LUXE : SELLER_ICON}
          alt=""
          aria-hidden="true"
          className={cx('sla-seller-icon')}
        />
        <span className={cx('sla-seller-name')}>{`Seller: ${sellerName}`}</span>
      </div>
      <p className={cx('sla-footer')}>
        {luxe || resolvedNote.startsWith('*') ? resolvedNote : `*${resolvedNote}`}
      </p>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  note: getB2cSellerConfig(state)?.sellerChangeNote
});

export default connect(mapStateToProps)(B2cSellerNoLocation);
